import { useEffect, useState } from 'react';
import {
  Mail, Instagram, Music2, MessageCircle, ExternalLink,
  Filter, Sparkles, Send, Archive, RefreshCw, X, Headphones, ChevronLeft,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../components/ui/ToastSystem';
import { AdminPageHeader } from '../../components/admin/AdminPageHeader';

interface ContactMessage {
  id: string;
  name: string;
  email: string;
  subject: string;
  message: string;
  status: 'new' | 'replied' | 'archived';
  replied_at: string | null;
  created_at: string;
}

const STATUS_LABELS: Record<string, string> = { new: 'Novo', replied: 'Respondido', archived: 'Arquivado' };
const STATUS_COLORS: Record<string, string> = { new: '#F5C842', replied: '#4ade80', archived: '#5A5347' };
const STATUS_BG:     Record<string, string> = {
  new:      'rgba(245,200,66,0.12)',
  replied:  'rgba(74,222,128,0.10)',
  archived: 'rgba(90,83,71,0.15)',
};

const SOCIAL_LINKS = [
  { label: 'Instagram DM',     icon: <Instagram size={18} />, href: 'https://instagram.com/direct/inbox/', color: '#f472b6' },
  { label: 'TikTok DM',        icon: <Music2 size={18} />,    href: 'https://www.tiktok.com/messages',   color: '#a78bfa' },
  { label: 'WhatsApp',         icon: <MessageCircle size={18} />, href: 'https://web.whatsapp.com',      color: '#4ade80' },
  { label: 'Email suporte',    icon: <Mail size={18} />,      href: 'mailto:suporte@professya.com',      color: '#60a5fa' },
];

// ── Support Tickets types ──────────────────────────────────────────────────

interface Ticket {
  id: string; subject: string; status: string; priority: string;
  created_at: string; updated_at: string; user_email?: string; user_name?: string;
}
interface TicketMessage {
  id: string; sender_type: 'user' | 'admin'; message: string; created_at: string;
}

const TICKET_STATUS_OPTIONS = ['open','in_progress','waiting_customer','resolved','closed'];
const TICKET_STATUS_LABELS: Record<string, string> = {
  open: 'Aberto', in_progress: 'Em andamento', waiting_customer: 'Aguardando cliente',
  resolved: 'Resolvido', closed: 'Fechado',
};
const TICKET_STATUS_COLORS: Record<string, string> = {
  open: '#60a5fa', in_progress: '#f59e0b', waiting_customer: '#c084fc',
  resolved: '#4ade80', closed: 'var(--text-muted)',
};

function SupportTicketsTab() {
  const { showToast } = useToast();
  const [tickets,      setTickets]    = useState<Ticket[]>([]);
  const [loading,      setLoading]    = useState(true);
  const [selected,     setSelected]   = useState<Ticket | null>(null);
  const [ticketMsgs,   setTicketMsgs] = useState<TicketMessage[]>([]);
  const [reply,        setReply]      = useState('');
  const [sending,      setSending]    = useState(false);
  const [statusFilter, setStatusFilter] = useState('open');
  const [updating,     setUpdating]   = useState(false);

  useEffect(() => { loadTickets(); }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadTickets() {
    setLoading(true);
    let q = supabase.from('support_tickets')
      .select('id, subject, status, priority, created_at, updated_at, profiles:user_id(email, full_name)')
      .order('updated_at', { ascending: false }).limit(100);
    if (statusFilter !== 'all') q = q.eq('status', statusFilter);
    const { data } = await q;
    setTickets((data ?? []).map((t: Record<string, unknown>) => {
      const profile = t.profiles as { email?: string; full_name?: string } | null;
      return { ...t, user_email: profile?.email, user_name: profile?.full_name } as Ticket;
    }));
    setLoading(false);
  }

  async function selectTicket(ticket: Ticket) {
    setSelected(ticket); setReply('');
    const { data } = await supabase.from('ticket_messages')
      .select('id, sender_type, message, created_at')
      .eq('ticket_id', ticket.id).order('created_at', { ascending: true });
    setTicketMsgs((data ?? []) as TicketMessage[]);
  }

  async function sendReply() {
    if (!selected || !reply.trim()) return;
    setSending(true);
    const { error } = await supabase.from('ticket_messages').insert({
      ticket_id: selected.id, sender_type: 'admin', message: reply.trim(),
    });
    setSending(false);
    if (error) { showToast({ type: 'error', message: 'Erro ao enviar resposta.' }); return; }
    setReply('');
    showToast({ type: 'success', message: 'Resposta enviada ✦' });
    const { data } = await supabase.from('ticket_messages')
      .select('id, sender_type, message, created_at')
      .eq('ticket_id', selected.id).order('created_at', { ascending: true });
    setTicketMsgs((data ?? []) as TicketMessage[]);
  }

  async function updateStatus(newStatus: string) {
    if (!selected) return;
    setUpdating(true);
    await supabase.from('support_tickets').update({ status: newStatus }).eq('id', selected.id);
    setUpdating(false);
    setSelected({ ...selected, status: newStatus });
    setTickets(prev => prev.map(t => t.id === selected.id ? { ...t, status: newStatus } : t));
    showToast({ type: 'success', message: `Status → ${TICKET_STATUS_LABELS[newStatus]}` });
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem', alignItems: 'center' }}>
        <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>Legenda:</span>
        {Object.entries(TICKET_STATUS_LABELS).map(([key, label]) => (
          <span key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: TICKET_STATUS_COLORS[key], display: 'inline-block' }} />
            {label}
          </span>
        ))}
      </div>
      <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        {['all', ...TICKET_STATUS_OPTIONS].map(s => (
          <button key={s} onClick={() => { setStatusFilter(s); setSelected(null); }}
            className={statusFilter === s ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}>
            {s === 'all' ? 'Todos' : TICKET_STATUS_LABELS[s]}
          </button>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 1.5fr' : '1fr', gap: '1.25rem', alignItems: 'start' }}>
        <div>
          {loading && <div className="card shimmer" style={{ height: '200px' }} />}
          {!loading && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {tickets.length === 0 && (
                <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
                  <p style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Nenhum ticket encontrado</p>
                </div>
              )}
              {tickets.map(ticket => (
                <button key={ticket.id} onClick={() => selectTicket(ticket)} style={{
                  width: '100%', textAlign: 'left', padding: '0.875rem 1rem',
                  border: `1px solid ${selected?.id === ticket.id ? 'var(--petrol)' : 'var(--border-subtle)'}`,
                  borderRadius: 'var(--radius-card)',
                  backgroundColor: selected?.id === ticket.id ? 'rgba(31,78,95,0.07)' : 'var(--bg-surface)', cursor: 'pointer',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.2rem' }}>
                    <span style={{ fontSize: '10px', padding: '1px 7px', borderRadius: '999px', fontWeight: 700,
                      backgroundColor: `${TICKET_STATUS_COLORS[ticket.status]}18`, color: TICKET_STATUS_COLORS[ticket.status] }}>
                      {TICKET_STATUS_LABELS[ticket.status] ?? ticket.status}
                    </span>
                  </div>
                  <p style={{ fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 0.1rem', fontSize: 'var(--text-label)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ticket.subject}</p>
                  <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', margin: 0 }}>
                    {ticket.user_email ?? ticket.user_name ?? '-'} · {new Date(ticket.updated_at).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>
        {selected && (
          <div className="card animate-fade-in" style={{ position: 'sticky', top: '2rem' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', marginBottom: '1rem' }}>
              <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setSelected(null)} aria-label="Fechar"><ChevronLeft size={16} /></button>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 0.1rem', fontSize: 'var(--text-label)' }}>{selected.subject}</p>
                <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', margin: 0 }}>
                  {selected.user_email} · Criado {new Date(selected.created_at).toLocaleDateString('pt-BR')}
                </p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
              {TICKET_STATUS_OPTIONS.map(s => (
                <button key={s} onClick={() => updateStatus(s)} disabled={updating || selected.status === s} style={{
                  padding: '2px 10px', fontSize: '11px', fontWeight: 600, borderRadius: '999px',
                  border: `1px solid ${TICKET_STATUS_COLORS[s]}44`,
                  backgroundColor: selected.status === s ? `${TICKET_STATUS_COLORS[s]}22` : 'transparent',
                  color: TICKET_STATUS_COLORS[s], cursor: selected.status === s ? 'default' : 'pointer', opacity: updating ? 0.6 : 1,
                }}>{TICKET_STATUS_LABELS[s]}</button>
              ))}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginBottom: '1rem', maxHeight: '320px', overflowY: 'auto' }}>
              {ticketMsgs.map(msg => (
                <div key={msg.id} style={{ padding: '0.6rem 0.875rem', borderRadius: 'var(--radius-md)',
                  backgroundColor: msg.sender_type === 'admin' ? 'rgba(31,78,95,0.07)' : 'var(--bg-elevated)',
                  borderLeft: `3px solid ${msg.sender_type === 'admin' ? 'var(--petrol)' : 'var(--border-subtle)'}` }}>
                  <p style={{ fontSize: '10px', color: msg.sender_type === 'admin' ? 'var(--petrol)' : 'var(--text-muted)', fontWeight: 700, marginBottom: '0.2rem' }}>
                    {msg.sender_type === 'admin' ? '✦ Admin' : 'Usuário'} · {new Date(msg.created_at).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
                  </p>
                  <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', margin: 0, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{msg.message}</p>
                </div>
              ))}
              {ticketMsgs.length === 0 && <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', fontStyle: 'italic' }}>Sem mensagens ainda.</p>}
            </div>
            <textarea className="form-input" value={reply} onChange={e => setReply(e.target.value)}
              placeholder="Escrever resposta ao usuário..." rows={3}
              style={{ resize: 'vertical', marginBottom: '0.5rem', fontSize: 'var(--text-label)' }} maxLength={2000} />
            <button className="btn btn-primary btn-sm" onClick={sendReply} disabled={sending || !reply.trim()}>
              {sending ? 'Enviando...' : 'Enviar resposta'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

export function AdminMessagesPage() {
  const { showToast } = useToast();
  const [tab,        setTab]        = useState<'forms' | 'tickets' | 'social'>('forms');
  const [messages,   setMessages]   = useState<ContactMessage[]>([]);
  const [loading,    setLoading]    = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [subjectFilter, setSubjectFilter] = useState<string>('all');
  const [selected,   setSelected]   = useState<ContactMessage | null>(null);

  // Reply composer state
  const [replyText,    setReplyText]    = useState('');
  const [suggesting,   setSuggesting]   = useState(false);
  const [sending,      setSending]      = useState(false);
  const [archiving,    setArchiving]    = useState(false);

  // Unread count for badge
  const [newCount, setNewCount] = useState(0);

  useEffect(() => { loadMessages(); }, [statusFilter, subjectFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadMessages() {
    setLoading(true);
    let q = supabase
      .from('contact_messages')
      .select('id, name, email, subject, message, status, replied_at, created_at')
      .order('created_at', { ascending: false })
      .limit(200);

    if (statusFilter !== 'all')  q = q.eq('status', statusFilter);
    if (subjectFilter !== 'all') q = q.eq('subject', subjectFilter);

    const { data } = await q;
    setMessages(data ?? []);

    // Count unread (status = 'new')
    const { count } = await supabase
      .from('contact_messages')
      .select('id', { count: 'exact', head: true })
      .eq('status', 'new');
    setNewCount(count ?? 0);

    setLoading(false);
  }

  function openMessage(msg: ContactMessage) {
    setSelected(msg);
    setReplyText('');
  }

  async function suggestReply() {
    if (!selected) return;
    setSuggesting(true);
    const { data } = await supabase.functions.invoke('suggest-reply', {
      body: { messageId: selected.id },
    });
    setSuggesting(false);
    if (data?.ok && data.data?.suggestion) {
      setReplyText(data.data.suggestion);
      showToast({ type: 'success', message: 'Sugestão gerada ✦' });
    } else {
      showToast({ type: 'error', message: data?.error ?? 'Erro ao gerar sugestão' });
    }
  }

  async function sendReply() {
    if (!selected || !replyText.trim()) return;
    setSending(true);
    const { data } = await supabase.functions.invoke('send-contact-reply', {
      body: { messageId: selected.id, replyText: replyText.trim() },
    });
    setSending(false);
    if (data?.ok) {
      showToast({ type: 'success', message: `Resposta enviada para ${selected.email}` });
      setSelected(null);
      loadMessages();
    } else {
      showToast({ type: 'error', message: data?.error ?? 'Erro ao enviar resposta' });
    }
  }

  async function archiveMessage(msgId: string) {
    setArchiving(true);
    await supabase.from('contact_messages').update({ status: 'archived' }).eq('id', msgId);
    setArchiving(false);
    if (selected?.id === msgId) setSelected(null);
    showToast({ type: 'info', message: 'Mensagem arquivada' });
    loadMessages();
  }

  const allSubjects = Array.from(new Set(messages.map(m => m.subject)));

  return (
    <div>
      <AdminPageHeader title="Mensagens" objective="Central de mensagens recebidas via formulário de contato do site. Responda dúvidas, arquive mensagens resolvidas e use a sugestão de IA para acelerar o atendimento. SLA recomendado: responder em até 24 horas úteis." />

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <h1 className="font-display" style={{ fontSize: '1.5rem', color: 'var(--text-primary)' }}>Mensagens</h1>
          {newCount > 0 && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              minWidth: '20px', height: '20px', padding: '0 6px',
              backgroundColor: '#F5C842', color: '#0C0B0F',
              fontSize: '11px', fontWeight: 700, borderRadius: '999px',
            }}>
              {newCount}
            </span>
          )}
        </div>
        <button className="btn btn-ghost btn-sm" onClick={loadMessages} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <RefreshCw size={13} /> Atualizar
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.35rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <button className={tab === 'forms' ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'} onClick={() => setTab('forms')}>
          <Mail size={13} style={{ marginRight: '0.35rem' }} />
          Formulários {newCount > 0 && tab !== 'forms' && `(${newCount})`}
        </button>
        <button className={tab === 'tickets' ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'} onClick={() => setTab('tickets')}>
          <Headphones size={13} style={{ marginRight: '0.35rem' }} />
          Tickets de Suporte
        </button>
        <button className={tab === 'social' ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'} onClick={() => setTab('social')}>
          <ExternalLink size={13} style={{ marginRight: '0.35rem' }} />
          Redes Sociais
        </button>
      </div>

      {/* ── Tab: Tickets ──────────────────────────────────────────────────────── */}
      {tab === 'tickets' && <SupportTicketsTab />}

      {/* ── Tab: Redes Sociais ─────────────────────────────────────────────────── */}
      {tab === 'social' && (
        <div>
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-caption)', marginBottom: '1.25rem' }}>
            Acesso rápido às caixas de mensagens em cada plataforma.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem' }}>
            {SOCIAL_LINKS.map(sl => (
              <a
                key={sl.label}
                href={sl.href}
                target="_blank"
                rel="noopener noreferrer"
                className="card"
                style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', textDecoration: 'none', padding: '1rem' }}
              >
                <span style={{ color: sl.color }}>{sl.icon}</span>
                <div>
                  <p style={{ margin: 0, fontWeight: 600, color: 'var(--text-primary)', fontSize: 'var(--text-label)' }}>{sl.label}</p>
                  <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-muted)' }}>Abrir →</p>
                </div>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* ── Tab: Formulários ──────────────────────────────────────────────────── */}
      {tab === 'forms' && (
        <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 420px' : '1fr', gap: '1rem', alignItems: 'start' }}>

          {/* Message list */}
          <div>
            {/* Filters */}
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
              <Filter size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
              <div style={{ display: 'flex', gap: '0.25rem' }}>
                {['all', 'new', 'replied', 'archived'].map(s => (
                  <button key={s} onClick={() => setStatusFilter(s)}
                    className={statusFilter === s ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}>
                    {s === 'all' ? 'Todos' : STATUS_LABELS[s]}
                  </button>
                ))}
              </div>
              {allSubjects.length > 1 && (
                <select
                  className="form-select"
                  value={subjectFilter}
                  onChange={e => setSubjectFilter(e.target.value)}
                  style={{ fontSize: 'var(--text-caption)', padding: '4px 8px', height: 'auto' }}
                >
                  <option value="all">Todos os assuntos</option>
                  {allSubjects.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              )}
            </div>

            {loading ? (
              <div className="card shimmer" style={{ height: '300px' }} />
            ) : (
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                {messages.length === 0 ? (
                  <p style={{ textAlign: 'center', color: 'var(--text-muted)', fontStyle: 'italic', padding: '2rem' }}>
                    Nenhuma mensagem encontrada
                  </p>
                ) : (
                  messages.map(msg => (
                    <button
                      key={msg.id}
                      onClick={() => openMessage(msg)}
                      style={{
                        width: '100%', display: 'flex', alignItems: 'flex-start', gap: '0.75rem',
                        padding: '0.875rem 1rem', background: 'none', border: 'none',
                        borderBottom: '1px solid var(--border-subtle)', cursor: 'pointer', textAlign: 'left',
                        backgroundColor: selected?.id === msg.id ? 'rgba(245,200,66,0.05)' : undefined,
                        borderLeft: selected?.id === msg.id ? '2px solid var(--petrol)' : '2px solid transparent',
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.2rem', gap: '0.5rem' }}>
                          <p style={{ margin: 0, fontWeight: 600, color: 'var(--text-primary)', fontSize: 'var(--text-label)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {msg.name}
                          </p>
                          <span style={{
                            fontSize: '10px', padding: '1px 7px', borderRadius: '999px', flexShrink: 0,
                            backgroundColor: STATUS_BG[msg.status],
                            color: STATUS_COLORS[msg.status],
                            fontWeight: 600,
                          }}>
                            {STATUS_LABELS[msg.status]}
                          </span>
                        </div>
                        <p style={{ margin: '0 0 0.25rem', fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          [{msg.subject}] {msg.message}
                        </p>
                        <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                          {msg.email} · {new Date(msg.created_at).toLocaleDateString('pt-BR')}
                          {msg.status === 'new' && (Date.now() - new Date(msg.created_at).getTime()) > 86400000 && (
                            <span style={{ fontSize: '10px', padding: '1px 5px', borderRadius: '999px', backgroundColor: 'rgba(239,68,68,0.15)', color: '#f87171', fontWeight: 700 }}>24h+</span>
                          )}
                        </p>
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Detail panel */}
          {selected && (
            <div style={{ position: 'sticky', top: '1rem' }}>
              <div className="card">
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1rem' }}>
                  <div>
                    <p style={{ margin: '0 0 0.15rem', fontWeight: 700, color: 'var(--text-primary)', fontSize: 'var(--text-label)' }}>
                      {selected.name}
                    </p>
                    <p style={{ margin: 0, fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>
                      {selected.email}
                    </p>
                  </div>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => setSelected(null)}
                    style={{ padding: '4px' }}
                  >
                    <X size={14} />
                  </button>
                </div>

                {/* Meta */}
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                  <span style={{
                    fontSize: '11px', padding: '2px 8px', borderRadius: '999px',
                    backgroundColor: 'rgba(31,78,95,0.12)', color: 'var(--petrol)',
                  }}>
                    {selected.subject}
                  </span>
                  <span style={{
                    fontSize: '11px', padding: '2px 8px', borderRadius: '999px',
                    backgroundColor: STATUS_BG[selected.status], color: STATUS_COLORS[selected.status],
                  }}>
                    {STATUS_LABELS[selected.status]}
                  </span>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                    {new Date(selected.created_at).toLocaleString('pt-BR')}
                  </span>
                </div>

                {/* Message body */}
                <div style={{
                  backgroundColor: 'var(--bg-base)', borderRadius: 'var(--radius-md)',
                  padding: '0.875rem', marginBottom: '1rem',
                  fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', lineHeight: 1.7,
                  whiteSpace: 'pre-wrap', maxHeight: '200px', overflowY: 'auto',
                }}>
                  {selected.message}
                </div>

                {/* Actions (only when not archived) */}
                {selected.status !== 'archived' && (
                  <>
                    {/* Reply textarea */}
                    <div style={{ marginBottom: '0.75rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
                        <label style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          Resposta
                        </label>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={suggestReply}
                          disabled={suggesting}
                          style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '11px', color: 'var(--petrol)' }}
                        >
                          <Sparkles size={12} />
                          {suggesting ? 'Gerando...' : '✦ Sugerir com IA'}
                        </button>
                      </div>
                      <textarea
                        className="form-input"
                        value={replyText}
                        onChange={e => setReplyText(e.target.value)}
                        placeholder="Escreva sua resposta aqui, ou clique em 'Sugerir com IA'..."
                        rows={6}
                        style={{ resize: 'vertical', fontSize: 'var(--text-caption)', lineHeight: 1.7 }}
                      />
                    </div>

                    {/* Send + Archive buttons */}
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={sendReply}
                        disabled={sending || replyText.trim().length < 10}
                        style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}
                      >
                        <Send size={13} />
                        {sending ? 'Enviando...' : 'Enviar resposta'}
                      </button>
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => archiveMessage(selected.id)}
                        disabled={archiving}
                        style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', color: 'var(--text-muted)' }}
                      >
                        <Archive size={13} />
                        {archiving ? '...' : 'Arquivar'}
                      </button>
                      <a
                        href={`mailto:${selected.email}?subject=Re: ${encodeURIComponent(selected.subject)}`}
                        className="btn btn-ghost btn-sm"
                        style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', color: 'var(--text-muted)', textDecoration: 'none' }}
                      >
                        <Mail size={13} />
                        Abrir no email
                      </a>
                    </div>
                  </>
                )}

                {selected.status === 'archived' && (
                  <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', fontStyle: 'italic' }}>
                    Esta mensagem está arquivada.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
